import { useEffect, useState } from "react";
import { X, Check, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { submitConsultation } from "../../lib/api";
import { BRAND } from "../../data/content";

const PROJECT_TYPES = [
  "Existing Home",
  "New Home",
  "Luxury Villa",
  "Apartment",
  "Office",
  "Hospitality",
];
const HOME_SIZES = ["2BHK", "3BHK", "4BHK", "Villa", "5,000+ sqft", "Commercial"];
const BUDGETS = ["Under ₹3L", "₹3L – ₹6L", "₹6L – ₹15L", "₹15L+", "Not sure yet"];

const initial = {
  name: "",
  phone: "",
  email: "",
  city: "",
  project_type: "",
  home_size: "",
  budget: "",
  message: "",
};

export default function ConsultationDialog({ open, onClose, sourcePage = "homepage" }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      // reset after close animation
      setTimeout(() => {
        setForm(initial);
        setDone(false);
      }, 400);
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.city || !form.project_type) {
      toast.error("Please share your name, phone, city and project type.");
      return;
    }
    try {
      setBusy(true);
      await submitConsultation({ ...form, source_page: sourcePage });
      setDone(true);
      toast.success("Thank you. A Genie advisor will reach you shortly.");
    } catch (err) {
      toast.error("Could not submit. Please try again or call us directly.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="consultation-dialog"
      className="fixed inset-0 z-[100] flex items-end lg:items-center justify-center"
    >
      {/* backdrop */}
      <div
        onClick={onClose}
        className="absolute inset-0 bg-black/80 backdrop-blur-md animate-fade"
      />

      <div className="relative w-full lg:max-w-[1080px] max-h-[94vh] lg:max-h-[88vh] overflow-y-auto no-scrollbar bg-[var(--gx-surface)] lg:rounded-3xl rounded-t-3xl border border-line shadow-2xl animate-rise">
        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="consultation-close"
          className="absolute top-4 right-4 lg:top-6 lg:right-6 w-10 h-10 rounded-full border border-line hover:border-gold hover:text-gold text-dim flex items-center justify-center transition-colors z-10"
        >
          <X className="w-4 h-4" />
        </button>

        {done ? (
          <div className="px-6 py-16 lg:py-24 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center mb-6">
              <Check className="w-6 h-6 text-gold" />
            </div>
            <p className="eyebrow mb-3">Request received</p>
            <h3 className="h2-display max-w-[18ch] mx-auto">Welcome to GenieX.</h3>
            <p className="lead mt-5 max-w-[44ch] mx-auto">
              A Genie advisor will reach out within 24 hours to design your private consultation.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4 text-[13px] text-dim">
              <a href={`tel:${BRAND.phoneRaw}`} className="flex items-center gap-2 hover:text-gold transition-colors">
                <Phone className="w-3.5 h-3.5 text-gold" /> {BRAND.phone}
              </a>
              <span className="text-mute">·</span>
              <a href={`mailto:${BRAND.email}`} className="flex items-center gap-2 hover:text-gold transition-colors">
                <Mail className="w-3.5 h-3.5 text-gold" /> {BRAND.email}
              </a>
            </div>
            <button onClick={onClose} className="btn-outline mt-12">
              Continue exploring
            </button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_1.2fr]">
            {/* Left aesthetic panel */}
            <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-[#16161B] via-[#0E0E12] to-[#08080A] border-r border-line">
              <div>
                <p className="eyebrow mb-5">A Private Consultation</p>
                <h2 className="h2-display max-w-[14ch]">
                  Let's design your intelligent home.
                </h2>
                <p className="lead mt-6 max-w-[36ch]">
                  Share a few details. A senior advisor will personally curate your experience —
                  walkthrough, demo, and a bespoke proposal.
                </p>
              </div>
              <ul className="mt-12 space-y-4 text-[13px] text-dim">
                {[
                  "Free home survey across Telangana & Andhra",
                  "Live demo of Dynamic Series",
                  "Transparent pricing in ₹",
                  "5-year warranty. Lifetime care.",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <div className="text-[12px] text-mute pt-10">{BRAND.phone} · {BRAND.email}</div>
            </div>

            {/* Right form */}
            <form onSubmit={submit} className="p-6 lg:p-12">
              <p className="eyebrow lg:hidden mb-3">Book a Consultation</p>
              <h3 className="text-2xl lg:text-3xl font-display tracking-tight">
                Tell us about your home.
              </h3>
              <p className="text-[13px] text-dim mt-2 lg:mt-3 max-w-[40ch]">
                We'll respond within 24 hours.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
                <Field label="Full Name *" testid="lead-name">
                  <input required value={form.name} onChange={setF("name")} className="lux-input" data-testid="lead-name-input" />
                </Field>
                <Field label="Phone *" testid="lead-phone">
                  <input required type="tel" value={form.phone} onChange={setF("phone")} placeholder="+91" className="lux-input" data-testid="lead-phone-input" />
                </Field>
                <Field label="City *" testid="lead-city">
                  <input required value={form.city} onChange={setF("city")} placeholder="e.g. Hyderabad" className="lux-input" data-testid="lead-city-input" />
                </Field>
                <Field label="Project Type *" testid="lead-project-type">
                  <select required value={form.project_type} onChange={setF("project_type")} className="lux-input" data-testid="lead-project-type-select">
                    <option value="">Select…</option>
                    {PROJECT_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </Field>
                <Field label="Email (optional)" testid="lead-email" className="sm:col-span-2">
                  <input type="email" value={form.email} onChange={setF("email")} className="lux-input" data-testid="lead-email-input" />
                </Field>
                <Field label="Approx. Home Size" testid="lead-home-size">
                  <select value={form.home_size} onChange={setF("home_size")} className="lux-input" data-testid="lead-home-size-select">
                    <option value="">Select…</option>
                    {HOME_SIZES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </Field>
                <Field label="Budget Range (₹)" testid="lead-budget">
                  <select value={form.budget} onChange={setF("budget")} className="lux-input" data-testid="lead-budget-select">
                    <option value="">Select…</option>
                    {BUDGETS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </Field>
                <Field label="Anything we should know? (optional)" testid="lead-message" className="sm:col-span-2">
                  <textarea rows={3} value={form.message} onChange={setF("message")} placeholder="Tell us about your home, timeline, or specific needs…" className="lux-input resize-none" data-testid="lead-message-input" />
                </Field>
              </div>

              <button
                type="submit"
                disabled={busy}
                data-testid="lead-submit"
                className="btn-gold w-full mt-8 disabled:opacity-60"
              >
                {busy ? "Submitting…" : "Request My Consultation"}
              </button>
              <p className="text-[11px] text-mute text-center mt-4">
                By submitting, you agree to receive a call from a GenieX advisor. We respect your privacy.
              </p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, testid, className = "" }) {
  return (
    <label className={`block ${className}`} data-testid={testid}>
      <span className="block text-[11px] tracking-[0.18em] uppercase text-mute mb-2">{label}</span>
      {children}
    </label>
  );
}
